import { Base64 } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import { PublicationType  } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import type { IPublicationMetadataItem } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import type { IWpPublication } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-wp";
import type { 
    IPublicationStyleFontFace, 
    IReaderDocument, 
    IReaderPublication, 
    IReaderViewOptions 
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { IEpubReaderPublicationOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import type { IPdfReaderPublicationOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-pdf";
import type { IWpAudiobookReaderPublicationOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-wp-audiobook";
import {
    type ICirrusReaderReadingProgressionTimeline, 
    type ICirrusReaderSyntheticPageListData, 
    type ICirrusReaderPublicationData, 
    type ICirrusReaderPublicationStyleOptions, 
    CirrusReaderPublicationLandmarkType
} from "../shared/CirrusReaderModel";
import { BlobUrlManager } from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import { useCirrusReaderLandmarkCollection, type CirrusLandmarksCollection } from "../shared/CirrusReaderLandmarkCollection";

type CirrusNavigationTree = ReturnType<typeof useCirrusNavigationTree>

export default defineNuxtPlugin({
    name: 'cirrus-reader-publication',
    dependsOn: [
        'cirrus-reader-reading-system'
    ],
    enforce: 'pre',
    async setup (_nuxtApp) {
        const { variant } = useAppVariant()
        if (variant.value !== 'reader') return {};
        
        console.info('[CirrusPlugin] CirrusReaderPublication.setup()');
        const cirrusReaderUtils = useCirrusReaderUtils();
        
        let _id: string = '';
        let _title: string = '';
        let _format: PublicationType;
        let _url: string = '';
        let _isRtl: boolean = false;
        let _isFixedLayout: boolean = false;
        let _hasMoSyncMedia: boolean = false;
        let _metadata: IPublicationMetadataItem[] = [];
        let _language: string | undefined = undefined;
        let _coverImageUrl: URL | undefined = undefined;
        let _coverImageData: string | undefined = undefined;

        let _progressionTimeline: ICirrusReaderReadingProgressionTimeline | undefined;
        let _navigationTree: CirrusNavigationTree;
        let _landmarksCollection: CirrusLandmarksCollection | undefined = undefined;

        const _blobUrlManager: BlobUrlManager = new BlobUrlManager();

        let _colibrioReaderPublication: IReaderPublication;
        let _publicationStyleOptions: ICirrusReaderPublicationStyleOptions | undefined;

        const initPublication = async (
            colibrioReaderPublication: IReaderPublication
        ) => {
            console.log("CirrusReaderPublication.initPublication");

            const _publicationIsFixedLayout = colibrioReaderPublication.getSpine().some((doc: IReaderDocument) => {
                return doc.getSourceContentDocument().getLayout() === 'PRE_PAGINATED';
            });

            _colibrioReaderPublication = colibrioReaderPublication;
            _id = colibrioReaderPublication.getSourcePublication().getMetadata().getIdentifiers()[0]?.content.value ?? "";
            _title = colibrioReaderPublication.getSourcePublication().getMetadata().getTitles()[0]?.content.value ?? "Untitled";
            _url = colibrioReaderPublication.getDefaultLocatorUrl();
            _format = colibrioReaderPublication.getSourcePublication().getType();
            _isFixedLayout = _publicationIsFixedLayout;
            _isRtl = colibrioReaderPublication.getSourcePublication().getPageProgressionDirection() === 'RTL';
            _hasMoSyncMedia = colibrioReaderPublication.getAvailableSyncMediaFormats().some(value => value === 'EPUB_MEDIA_OVERLAY');
            _metadata = colibrioReaderPublication.getSourcePublication().getMetadata().getAll();
            _language = (colibrioReaderPublication.getSourcePublication().getMetadata().getLanguages().length > 0) ?
                colibrioReaderPublication.getSourcePublication().getMetadata().getLanguages()[0]!.content.value || undefined : undefined;
        
            _navigationTree = useCirrusNavigationTree();

            if (cirrusReaderUtils.isEpubReaderPublication(_colibrioReaderPublication) || cirrusReaderUtils.isWpAudiobookReaderPublication(_colibrioReaderPublication)) {
                _coverImageUrl = _colibrioReaderPublication.getSourcePublication().getCoverImageResourceUrl() || undefined;
            }
        }

        const buildNavigationTree = async (timeline?: ICirrusReaderReadingProgressionTimeline) => {
            if (!_navigationTree.hasBeenBuilt.value) {
                const navigation = await _colibrioReaderPublication.fetchPublicationNavigation();
                await _navigationTree.buildCirrusReaderNavigationTree(navigation, timeline);
            }
            useCirrusReaderNavigation().setNavigationTree(_navigationTree.treeData);
        }

        const buildPageList = async (timeline: ICirrusReaderReadingProgressionTimeline) => {
            if (_format === PublicationType.WP) {
                console.log('CirrusReaderPublication.buildPageList: Not building pagelist since this is an audiobook.');
                return;
            }
    
            if (!_navigationTree.hasBeenBuilt.value) {
                await buildNavigationTree();
            }
    
            useCirrusPageListStore().initialisePageList(timeline);
            await useCirrusPageListStore().buildPageList();
        }

        const fetchResourceBlobUrl = async (url: string, forceDataUrl: boolean = false): Promise<string | undefined> => {
            let resource;

            if (useCirrusReaderUtils().isEpubReaderPublication(_colibrioReaderPublication)) {
                resource = await _colibrioReaderPublication.getSourcePublication().getEpubResourceProvider().fetch(url);
            }

            if (resource) {
                const resourceData = await resource.asUint8Array();
                const mediaType = resource.getMetadata().mediaType;
                if (mediaType) {
                    return _blobUrlManager.createUrl([resourceData], mediaType, forceDataUrl).url;
                }
            }
            return undefined;
        }

        const fetchLandmarkCollection = async (timeline?: ICirrusReaderReadingProgressionTimeline, landmarkTypesToInclude?: CirrusReaderPublicationLandmarkType[]): Promise<CirrusLandmarksCollection | undefined> => {
            if (_landmarksCollection) {
                return _landmarksCollection;
            }

            _landmarksCollection = useCirrusReaderLandmarkCollection(timeline);

            landmarkTypesToInclude = (landmarkTypesToInclude && landmarkTypesToInclude.length > 0) ? landmarkTypesToInclude : [
                CirrusReaderPublicationLandmarkType.FIGURE,
                CirrusReaderPublicationLandmarkType.IMAGE,
                CirrusReaderPublicationLandmarkType.ORDERED_LIST,
                CirrusReaderPublicationLandmarkType.UNORDERED_LIST,
                CirrusReaderPublicationLandmarkType.DESCRIPTION_LIST,
                CirrusReaderPublicationLandmarkType.TABLE,
                CirrusReaderPublicationLandmarkType.HEADING1,
                CirrusReaderPublicationLandmarkType.HEADING2,
                CirrusReaderPublicationLandmarkType.HEADING3,
                CirrusReaderPublicationLandmarkType.HEADING4,
                CirrusReaderPublicationLandmarkType.HEADING5,
                CirrusReaderPublicationLandmarkType.HEADING6,
                CirrusReaderPublicationLandmarkType.FOOTNOTE,
                CirrusReaderPublicationLandmarkType.LINK,
            ];

            await _landmarksCollection.populate(undefined, landmarkTypesToInclude);

            return _landmarksCollection;
        }

        const _fetchCoverImageAsBase64 = async (): Promise<string | undefined> => {
            if (_coverImageData) {
                return _coverImageData;
            }
    
            if (cirrusReaderUtils.isEpubReaderPublication(_colibrioReaderPublication) && _coverImageUrl) {
                const imageResource = await _colibrioReaderPublication.getSourcePublication().getEpubResourceProvider().fetch(_coverImageUrl);
                if (imageResource) {
                    _coverImageData = Base64.encode(await imageResource.asUint8Array());
                    return _coverImageData;
                }
            } else if (cirrusReaderUtils.isWpAudiobookReaderPublication(_colibrioReaderPublication)) {
                const publication = _colibrioReaderPublication.getSourcePublication() as IWpPublication;
                const coverImageResourceUrl = publication.getCoverImageResourceUrl();
                if (coverImageResourceUrl) {
                    const coverImageResponse = await publication.getBackingResourceProvider().fetch(coverImageResourceUrl);
                    _coverImageData = Base64.encode(await coverImageResponse.asUint8Array());
                    return _coverImageData;
                }
            }
            return undefined;
        }

        const setPublicationStyleOptions = (
            styleOptions: ICirrusReaderPublicationStyleOptions,
        ): IReaderViewOptions => {
            
            const colibrioReaderViewOptions: IReaderViewOptions = {};
            colibrioReaderViewOptions.publicationStyleOptions = {
                defaultFontFamily: styleOptions.defaultFontFamily,
                fontSizeScaleFactor: styleOptions.fontSizeScaleFactor,
                lineHeightScaleFactor: styleOptions.lineHeightScaleFactor,
                letterSpacing: styleOptions.letterSpacing,
                wordSpacing: styleOptions.wordSpacing,
                textAlignment: styleOptions.textAlignment || null,
                pageMargins: styleOptions.pageMargins,
                palette: styleOptions.palette || null,
            }
            if(styleOptions.fontSet) {
                const fontFaces: IPublicationStyleFontFace[] = [];
                for (const fontFace of styleOptions.fontSet.fontFaces) {
                    const fontSetAsset = useCirrusReaderSettingsStore().availableFonts.find(fontSet => fontSet.fontFaces && fontSet.fontFaces[0]?.family === fontFace.family);
                    fontFaces.push({
                        family: fontFace.family,
                        fontWeight: fontFace.fontWeight,
                        fontStyle: fontFace.fontStyle,
                        fontStretch: fontFace.fontStretch,
                        mediaType: fontFace.mediaType,
                        src: fontSetAsset?.fontFaces[0]?.src || '',
                        srcUrl: fontSetAsset?.fontFaces[0]?.srcUrl || '',
                    });
                }

                colibrioReaderViewOptions.publicationStyleOptions.fontSet = {
                    defaults: styleOptions.fontSet.defaults,
                    selectors: styleOptions.fontSet.selectors,
                    fontFaces
                }
            } else {
                colibrioReaderViewOptions.publicationStyleOptions.fontSet = null;
            }

            return colibrioReaderViewOptions;
        }
    

        return {
            provide: {
                cirrusReaderPublication: {
                    initPublication,
                    buildNavigationTree,
                    buildPageList,
                    fetchResourceBlobUrl,
                    fetchLandmarkCollection,
                    serialize: async(): Promise<ICirrusReaderPublicationData> => {
                        return {
                            id: _id,
                            fileSourceUri: '',
                            isFixedLayout: _isFixedLayout,
                            url: _url,
                            title: _title,
                            isRtl: _isRtl,
                            hasMoSyncMedia: _hasMoSyncMedia,
                            isAudiobook: _format === PublicationType.WP,
                            format: _format,
                            metadata: _colibrioReaderPublication.getSourcePublication().getMetadata().toJSON(),
                            language: _language,
                            coverImageAsBase64: _coverImageData || await _fetchCoverImageAsBase64(),
                        };
                    },
                    set publicationStyleOptions(styleOptions: ICirrusReaderPublicationStyleOptions | undefined) {
                        _publicationStyleOptions = styleOptions;
                    },
                    get publicationStyleOptions(): ICirrusReaderPublicationStyleOptions | undefined {
                        return _publicationStyleOptions;
                    },
                    setPublicationStyleOptions,
                    get id(): string {
                        return _id;
                    },
                    get fileSourceUri(): string {
                        return '';
                    },
                    get title(): string {
                        return _title;
                    },
                    get url(): string {
                        return _url;
                    },
                    get format(): PublicationType {
                        return _format;
                    },
                    get isRtl(): boolean {
                        return _isRtl;
                    },
                    get isFixedLayout(): boolean {
                        return _isFixedLayout;
                    },
                    get isAudiobook(){
                        return _format === PublicationType.WP;
                    },
                    get hasMoSyncMedia() {
                        return _hasMoSyncMedia;
                    },
                    get metadata(): IPublicationMetadataItem[] {
                        return _metadata;
                    },
                    get language(): string | undefined {
                        return _language;
                    },
                    get navigationTree(): CirrusNavigationTree {
                        return _navigationTree;
                    },
                    get pageListData(): ICirrusReaderSyntheticPageListData | undefined {
                        return useCirrusPageListStore().serialize();
                    },
                    get progressionTimeline(): ICirrusReaderReadingProgressionTimeline | undefined {
                        return _progressionTimeline;
                    },
                    set progressionTimeline(instance: ICirrusReaderReadingProgressionTimeline | undefined) {
                        _progressionTimeline = instance;
                    },
                    get colibrioReaderPublication(): IReaderPublication {
                        return _colibrioReaderPublication;
                    },
                    set publicationOptions(colibrioEpubPublicationOptionData: IEpubReaderPublicationOptions | IPdfReaderPublicationOptions | IWpAudiobookReaderPublicationOptions) {
                        if (cirrusReaderUtils.isEpubReaderPublication(_colibrioReaderPublication)) {
                            _colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
                        } else if (cirrusReaderUtils.isPdfReaderPublication(this.colibrioReaderPublication)) {
                            _colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
                        } else if (cirrusReaderUtils.isWpAudiobookReaderPublication(this.colibrioReaderPublication)) {
                            _colibrioReaderPublication.setOptions(colibrioEpubPublicationOptionData);
                        }
                
                    }
                }
            }
        };
    }
});